import { CategoryData, SectionNames } from '@/types'

/**
 * Parse category string in format "Domain: Skill" and return domain and skill
 * Handles edge cases:
 * - Multiple colons: Only splits on the first ": " occurrence
 * - No colon: Treats entire string as domain with empty skill
 * - Empty strings: Returns empty domain and skill
 */
export const parseCategory = (category: string) => {
  if (!category) {
    return {
      domain: '',
      skill: '',
    }
  }

  // Check if the category contains ": " separator
  const separatorIndex = category.indexOf(': ')

  if (separatorIndex === -1) {
    // No separator found - treat entire string as domain only
    return {
      domain: category.trim(),
      skill: '',
    }
  }

  // Split only on the first ": " occurrence
  const domain = category.substring(0, separatorIndex).trim()
  const skill = category.substring(separatorIndex + 2).trim()

  return {
    domain,
    skill,
  }
}

/**
 * Create chart data from category counts
 * Extracts domain from category string for domain-level aggregation
 * - If category contains ": ", splits on first occurrence to get domain
 * - If no ": " found, uses entire string as domain
 */
export const createChartData = (totalData: Record<string, number>, correctData: Record<string, number>) => {
  const skillData: CategoryData = {}
  const domainData: CategoryData = {}

  // Process total data
  for (const [category, totalValue] of Object.entries(totalData)) {
    // Category stats (use full category string as key)
    skillData[category] = { total: totalValue, correct: 0 }

    // Extract domain from category string
    const separatorIndex = category.indexOf(': ')
    const domain = separatorIndex === -1 ? category.trim() : category.substring(0, separatorIndex).trim()

    if (!domainData[domain]) {
      domainData[domain] = { total: 0, correct: 0 }
    }
    domainData[domain].total += totalValue
  }

  // Process correct data
  for (const [category, correctValue] of Object.entries(correctData)) {
    // Category stats
    if (skillData[category]) {
      skillData[category].correct = correctValue
    } else {
      console.warn(`Category ${category} found in correctData but not in totalData`)
    }

    // Extract domain from category string
    const separatorIndex = category.indexOf(': ')
    const domain = separatorIndex === -1 ? category.trim() : category.substring(0, separatorIndex).trim()

    if (domainData[domain]) {
      domainData[domain].correct += correctValue
    }
  }

  return { skillData, domainData }
}

/**
 * Initialized raw data
 */
export const initializeRawData = (name: SectionNames) => {
  return { name, raw: 0, correct: 0, wrong: 0, skip: 0, scale: 0, total: 0 }
}

// Helper to normalize answers (remove trailing zeros from numbers)
export const normalizeAnswer = (answer: string): string => {
  const trimmed = answer.trim()
  const num = parseFloat(trimmed)
  // If it's a valid number, convert back to string to remove trailing zeros
  if (!isNaN(num)) {
    return String(num)
  }
  return trimmed
}
